import DialogContent from '@mui/material/DialogContent'
import { DataEditContextNumberField } from 'Components/reusable/DataContext/fields/DataEditContextNumberField'
import { DataEditContextExperienceTimeField } from 'Components/reusable/DataContext/fields/DataEditContextExperienceTimeField'
import { DataEditContextSkillField } from 'Components/reusable/DataContext/fields/DataEditContextSkillField'
import { DataEditContextSkillLevelField } from 'Components/reusable/DataContext/fields/DataEditContextSkillLevelField'
import { DataEditContextInterestLevelField } from 'Components/reusable/DataContext/fields/DataEditContextInterestLevelField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import Grid from '@mui/material/Grid2'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ISkillOrIndustryOrRole, IPersonSkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { SkillId } from 'types/ids'
import AddToArrayButton from 'Components/reusable/Buttons/AddToArrayButton'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import SkillsItem from './SkillsItem'

/** @notExported  */
interface ISkillModalProps {
  /** Skill being edited. */
  item: IPersonSkillOrIndustryOrRole | null
  /** Skill name. */
  name?: string
  /** Already added skills. */
  usedSkills?: IPersonSkillOrIndustryOrRole[]
  /** Multiple skills being edited. */
  multipleItems: IPersonSkillOrIndustryOrRole[]
  /** Function to add multiple skills. */
  addMultiple: () => void
  /** Function to remove skills from array. */
  removeFromArray: (items: IPersonSkillOrIndustryOrRole[]) => void
  /** Skill type. */
  type: string
  /** Edit mode. */
  edit: boolean
}

/**
 * Skill modal.
 *
 * @returns Skill modal component.
 * @notExported
 */
const SkillModal: React.FC<ISkillModalProps> = ({
  item,
  name,
  usedSkills,
  multipleItems,
  addMultiple,
  removeFromArray,
  type,
  edit,
}) => {
  const { t } = useTranslation()

  const handleSkillDelete = (id: SkillId) => {
    const newSkillsData = multipleItems.filter(item => item.skillId !== id)
    removeFromArray(newSkillsData)
  }

  const getDisabledOptions = () => {
    const disabledOptions: ISkillOrIndustryOrRole[] = []
    usedSkills?.filter(skill => skill.id !== item?.id).map(item => disabledOptions.push(item.Skill))
    return disabledOptions
  }

  return (
    <Grid container>
      <DialogContent>
        <Grid>
          <DataEditContextSkillField<IPersonSkillOrIndustryOrRole>
            field="skillId"
            dataField="Skill"
            required
            fullWidth
            usedSkills={getDisabledOptions()}
            name={name}
            type={type}
            insetLabel
            disabled={edit}
          />
          <DataEditContextExperienceTimeField<IPersonSkillOrIndustryOrRole>
            field="experienceMonths"
            required
            fullWidth
          />
          <DataEditContextNumberField<IPersonSkillOrIndustryOrRole> field="lastUsed" required fullWidth insetLabel />
          <DataEditContextSkillLevelField<IPersonSkillOrIndustryOrRole> field="level" required fullWidth insetLabel />
          <DataEditContextInterestLevelField<IPersonSkillOrIndustryOrRole> field="interestLevel" fullWidth insetLabel />
          {!item ? (
            <DataEditContextValidation>
              {({ valid }) => (
                <AddToArrayButton
                  valid={valid}
                  clickAction={() => addMultiple()}
                  label={t(`profile.${type}.modal.addSkill`)}
                />
              )}
            </DataEditContextValidation>
          ) : null}
        </Grid>
      </DialogContent>
      {multipleItems.length > 0 ? (
        <Grid sx={{ minWidth: '150px', paddingTop: '20px', paddingRight: '10px' }}>
          {multipleItems.map((skill, i) =>
            !item ? (
              <SkillsItem
                key={skill.id ?? `new-${i}`}
                editable
                newChip={true}
                showTooltip={true}
                showExperience={true}
                showLastUsed={true}
                skill={skill as IPersonSkillOrIndustryOrRole}
                onDeleteClick={() => handleSkillDelete(skill.skillId)}
                type={type}
              />
            ) : null
          )}
        </Grid>
      ) : null}
    </Grid>
  )
}

export default newWithEditModal(SkillModal)
